import algoliaInsights from 'search-insights'
import settings from '@/settings'
import store from '@/store/index'

const sendAlgoliaEvent = (type, data) => {
  if (typeof window !== 'undefined' && window.dataLayer) {
    const GTM_EVENT_NAME = 'algolia_event'
    algoliaInsights('getUserToken', {}, (_, userToken) => {
      const payload = {
        ...data,
        userToken
      }

      if (store.state.user?.userData?.id) {
        payload.authenticatedUserToken = store.state.user.userData.id
      }

      // Push a null payload to force GTM to recognize a new event
      window.dataLayer.push({ payload: null })

      window.dataLayer.push({
        event: GTM_EVENT_NAME,
        type,
        payload
      })
    })
  }
}

export default {
  googleEnabled: !!(settings.services.analytics.google.enabled && settings.services.analytics.google.accessToken),
  oneTrustEnabled: settings.services.oneTrust.enabled,
  algoliaAnalyticsEnabled: settings.services.algolia.analyticsEnabled,
  init () {
    if (this.oneTrustEnabled) {
      const productionLikeEnvs = ['prod', 'stage']
      const environmentPostfix = productionLikeEnvs.includes(settings.app.env) ? '' : '-test'
      const cookieConsentScript = document.createElement('script')
      cookieConsentScript.src = `https://cdn.cookielaw.org/consent/142a2985-02d0-4231-9138-a4f20b82857b${environmentPostfix}/OtAutoBlock.js`
      cookieConsentScript.type = 'text/javascript'
      document.head.appendChild(cookieConsentScript)

      const cookieScript = document.createElement('script')
      cookieScript.src = 'https://cdn.cookielaw.org/scripttemplates/otSDKStub.js'
      cookieScript.type = 'text/javascript'
      cookieScript.setAttribute('data-domain-script', `142a2985-02d0-4231-9138-a4f20b82857b${environmentPostfix}`)
      cookieScript.setAttribute('data-language', store.getters['locale/getLocaleCode'])
      document.head.appendChild(cookieScript)

      window.OptanonWrapper = function OptanonWrapper () { }
    }
    if (this.googleEnabled) {
      window.dataLayer = window.dataLayer || []
      window.dataLayer.push(
        {
          brandCode: `STORE${settings.app.country === 'US' ? '' : `_${settings.app.country}`}`,
          Site: 'Hubbell Store'
        },
        {
          'gtm.start': new Date().getTime(),
          event: 'gtm.js'
        }
      )

      const gtmScript = document.createElement('script')
      gtmScript.src = `/metrics/?id=${settings.services.analytics.google.accessToken}`
      gtmScript.async = true
      gtmScript.defer = true
      gtmScript.type = 'text/javascript'
      document.head.appendChild(gtmScript)

      const noScriptWrapper = document.createElement('noscript')
      const gtmIframe = document.createElement('iframe')
      gtmIframe.src = `/metrics/ns.html/?id=${settings.services.analytics.google.accessToken}`
      gtmIframe.height = 0
      gtmIframe.width = 0
      gtmIframe.style.display = 'none'
      gtmIframe.style.visibility = 'hidden'

      noScriptWrapper.appendChild(gtmIframe)
      document.body.appendChild(noScriptWrapper)

      if (settings.services.analytics.google.accessTokenCA !== '') {
        const gtmScript = document.createElement('script')
        gtmScript.src = `/metrics/?id=${settings.services.analytics.google.accessTokenCA}`
        gtmScript.async = true
        gtmScript.defer = true
        gtmScript.type = 'text/javascript'
        document.head.appendChild(gtmScript)

        const noScriptWrapper = document.createElement('noscript')
        const gtmIframe = document.createElement('iframe')
        gtmIframe.src = `/metrics/ns.html/?id=${settings.services.analytics.google.accessTokenCA}`
        gtmIframe.height = 0
        gtmIframe.width = 0
        gtmIframe.style.display = 'none'
        gtmIframe.style.visibility = 'hidden'

        noScriptWrapper.appendChild(gtmIframe)
        document.body.appendChild(noScriptWrapper)
      }
    }

    if (settings.services.analytics.hubspotScript.enabled) {
      window._hsq = window._hsq || []
      window._hsq.push(['setPath', window.location.pathname])

      const hubspotScript = document.createElement('script')
      hubspotScript.src = `//js.hs-scripts.com/${settings.services.analytics.hubspotScript.id}.js`
      hubspotScript.id = 'hs-script-loader'
      hubspotScript.async = true
      hubspotScript.defer = true
      hubspotScript.type = 'text/javascript'
      document.head.appendChild(hubspotScript)
    }

    if (this.algoliaAnalyticsEnabled) {
      algoliaInsights('init', {
        appId: settings.services.algolia.appId,
        apiKey: settings.services.algolia.accessToken,
        useCookie: true
      })

      window.aa = algoliaInsights
    }
  },
  sendPlaceOrderData ({ orderData, productsData, generalData }) {
    if (this.googleEnabled) {
      window.dataLayer.push({ ecommerce: null })
      window.dataLayer.push({
        event: 'transaction',
        ecommerce: {
          ...generalData,
          purchase: {
            actionField: orderData,
            products: productsData
          }
        }
      })
    }
  },
  sendImpressionsData ({ productsData, generalData }) {
    if (this.googleEnabled) {
      window.dataLayer.push({ ecommerce: null })
      window.dataLayer.push({
        ecommerce: {
          ...generalData,
          impressions: productsData
        }
      })
    }
  },
  sendImpressionsViewItemData ({ productsData }) {
    if (this.googleEnabled) {
      window.dataLayer.push({ ecommerce: null })
      window.dataLayer.push({
        event: 'view_item',
        ecommerce: {
          items: productsData
        }
      })
    }
  },
  sendImpressionsViewItemsData ({ productsData }) {
    if (this.googleEnabled) {
      window.dataLayer.push({ ecommerce: null })
      window.dataLayer.push({
        event: 'view_item_list',
        ecommerce: {
          items: productsData
        }
      })
    }
  },
  sendProductDetailsData ({ orderData, productsData, generalData }) {
    if (this.googleEnabled) {
      window.dataLayer.push({ ecommerce: null })
      window.dataLayer.push({
        event: 'productDetailView',
        ecommerce: {
          ...generalData,
          detail: {
            actionField: orderData,
            products: productsData
          }
        }
      })
    }
  },
  sendProductDetailsClickData ({ orderData, productsData }) {
    if (this.googleEnabled) {
      window.dataLayer.push({ ecommerce: null })
      window.dataLayer.push({
        event: 'productClick',
        ecommerce: {
          click: {
            actionField: orderData,
            products: productsData
          }
        }
      })
    }
  },
  sendAddProductToCartData ({ generalData, productsData }) {
    if (this.googleEnabled) {
      window.dataLayer.push({ ecommerce: null })
      window.dataLayer.push({
        event: 'add_to_cart',
        ecommerce: {
          ...generalData,
          items: productsData
        }
      })
    }
  },
  sendRemoveProductsFromCartData ({ generalData, productsData }) {
    if (this.googleEnabled) {
      window.dataLayer.push({ ecommerce: null })
      window.dataLayer.push({
        event: 'remove_from_cart',
        ecommerce: {
          ...generalData,
          items: productsData
        }
      })
    }
  },
  sendSelectProductFromListData ({ productsData }) {
    if (this.googleEnabled) {
      window.dataLayer.push({ ecommerce: null })
      window.dataLayer.push({
        event: 'select_item',
        ecommerce: {
          items: productsData
        }
      })
    }
  },
  sendCheckoutStepsData ({ orderData, productsData }) {
    if (this.googleEnabled) {
      window.dataLayer.push({ ecommerce: null })
      window.dataLayer.push({
        event: 'checkout',
        ecommerce: {
          checkout: {
            actionField: orderData,
            products: productsData
          }
        }
      })
    }
  },
  sendPromotionClickData ({ promotionData }) {
    if (this.googleEnabled) {
      window.dataLayer.push({ ecommerce: null })
      window.dataLayer.push({
        event: 'promotionClick',
        ecommerce: {
          promoClick: {
            promotions: [promotionData]
          }
        }
      })
    }
  },
  sendSelectPromotionData ({ promotionData }) {
    if (this.googleEnabled) {
      window.dataLayer.push({ ecommerce: null })
      window.dataLayer.push({
        event: 'select_promotion',
        ecommerce: {
          items: [promotionData]
        }
      })
    }
  },
  sendPromotionImpressionData ({ promotionData }) {
    if (this.googleEnabled) {
      window.dataLayer.push({ ecommerce: null })
      window.dataLayer.push({
        event: 'promotionView',
        ecommerce: {
          promoView: {
            promotions: [promotionData]
          }
        }
      })
    }
  },
  sendViewPromotionImpressionData ({ promotionData }) {
    if (this.googleEnabled) {
      window.dataLayer.push({ ecommerce: null })
      window.dataLayer.push({
        event: 'view_promotion',
        ecommerce: {
          items: promotionData
        }
      })
    }
  },
  sendAddProductToListData ({ generalData, productsData }) {
    if (this.googleEnabled) {
      window.dataLayer.push({ ecommerce: null })
      window.dataLayer.push({
        event: 'add_to_wishlist',
        ecommerce: {
          ...generalData,
          items: productsData
        }
      })
    }
  },

  sendViewReviewCartData ({ generalData, productsData }) {
    if (this.googleEnabled) {
      window.dataLayer.push({ ecommerce: null })
      window.dataLayer.push({
        event: 'view_cart',
        ecommerce: {
          ...generalData,
          items: productsData
        }
      })
    }
  },

  sendBeginCheckoutData ({ generalData, productsData }) {
    if (this.googleEnabled) {
      window.dataLayer.push({ ecommerce: null })
      window.dataLayer.push({
        event: 'begin_checkout',
        ecommerce: {
          ...generalData,
          items: productsData
        }
      })
    }
  },

  sendShippingCheckoutData ({ generalData, productsData }) {
    if (this.googleEnabled) {
      window.dataLayer.push({ ecommerce: null })
      window.dataLayer.push({
        event: 'add_shipping_info',
        ecommerce: {
          ...generalData,
          items: productsData
        }
      })
    }
  },

  sendPaymentInformationCheckoutData ({ generalData, productsData }) {
    if (this.googleEnabled) {
      window.dataLayer.push({ ecommerce: null })
      window.dataLayer.push({
        event: 'add_payment_info',
        ecommerce: {
          ...generalData,
          items: productsData
        }
      })
    }
  },

  sendPurchaseCheckoutData ({ generalData, productsData }) {
    if (this.googleEnabled) {
      window.dataLayer.push({ ecommerce: null })
      window.dataLayer.push({
        event: 'purchase',
        ecommerce: {
          ...generalData,
          items: productsData
        }
      })
    }
  },

  sendSearchClick ({ indexName, queryID, objectIDs, positions }) {
    if (this.algoliaAnalyticsEnabled) {
      sendAlgoliaEvent('clickedObjectIDsAfterSearch', {
        eventName: 'Search Result Product Clicked',
        index: indexName,
        queryID,
        objectIDs,
        positions
      })
    }
  },

  sendSearchClickedFilters ({ indexName, filters }) {
    if (this.algoliaAnalyticsEnabled) {
      sendAlgoliaEvent('clickedFilters', {
        eventName: 'Search Result Facet Clicked',
        index: indexName,
        filters
      })
    }
  },

  sendSearchConvertedFilters ({ indexName, filters }) {
    if (this.algoliaAnalyticsEnabled) {
      sendAlgoliaEvent('convertedFilters', {
        eventName: 'Search Result Facet Converted',
        index: indexName,
        filters
      })
    }
  },

  sendViewedProduct ({ objectIDs }) {
    if (this.algoliaAnalyticsEnabled) {
      sendAlgoliaEvent('viewedObjectIDs', {
        eventName: 'Product Viewed',
        index: settings.services.algolia.productIndexRelevance,
        objectIDs
      })
    }
  },

  sendAddedToCart ({ indexName, queryID, objectIDs, objectData, currency }) {
    if (this.algoliaAnalyticsEnabled) {
      const algoliaInsightEvent = queryID ? 'addedToCartObjectIDsAfterSearch' : 'addedToCartObjectIDs'
      const eventName = queryID ? 'Search Result Product Added to Cart' : 'Product Added to Cart'
      sendAlgoliaEvent(algoliaInsightEvent, {
        eventName,
        index: indexName || settings.services.algolia.productIndexRelevance,
        queryID,
        objectIDs,
        objectData,
        currency
      })
    }
  },

  sendSearchOrderConversion (conversionObjects) {
    if (this.algoliaAnalyticsEnabled) {
      conversionObjects.forEach(({ indexName, queryID, objectIDs, objectData, currency }) => {
        if (objectIDs?.length) {
          const algoliaInsightEvent = queryID ? 'purchasedObjectIDsAfterSearch' : 'purchasedObjectIDs'
          const eventName = queryID ? 'Search Result Product Purchased' : 'Product Purchased'
          sendAlgoliaEvent(algoliaInsightEvent, {
            eventName,
            index: indexName || settings.services.algolia.productIndexRelevance,
            queryID,
            objectIDs,
            objectData,
            currency
          })
        }
      })
    }
  },
  trackHubspotPageView (pageUrl) {
    if (settings.services.analytics.hubspotScript.enabled) {
      window._hsq.push(['setPath', pageUrl])
      window._hsq.push(['trackPageView'])
    }
  }
}
